import { urgencyFeeCents, urgencyMode, type PricingData } from '@rpl/pricing';
import { formatMoney } from '../../lib/format';
import { pricedPrints } from '../../lib/sizePaper';
import { computeUrgencyEta, parseCutoffMinutes, sydneyNow } from '../../lib/urgencyEta';
import { toPrintSelections, useStore, useTotals } from '../../store/store';
import { CartModal } from './CartModal';

/** "13:30" -> "1:30pm". */
function formatCutoff(hhmm: string): string {
  const [h, m] = (hhmm || '13:30').split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h)) return '1:30pm';
  const ap = h < 12 ? 'am' : 'pm';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, '0')}${ap}`;
}

/**
 * Urgent-processing upsell dialog (cart page). The mode comes from the LIVE
 * prints via urgencyMode: C-Type-only orders pay the surcharge at checkout
 * ('paid') and get the same-day/next-day ETA table below; anything with fine
 * art / custom sizes / frames may only REQUEST urgency ('request') — the fee is
 * quoted here and invoiced after staff confirm turnaround.
 *
 * ETA table is day-aware via computeUrgencyEta: the lab prints on
 * pricing.urgency.productionDays only (default Mon-Fri), so the start day rolls
 * past the cutoff AND non-production days (Fri after cutoff -> Monday), and per
 * collect point: all printing is in Sydney, so Wollongong pickup is +1
 * production day (sent down overnight). Surcharge + cutoff + production days
 * come from pricing.urgency (admin-editable).
 */
export function UrgencyDialog({
  pricing,
  onClose,
}: {
  pricing: PricingData;
  onClose: () => void;
}) {
  const urgent = useStore((s) => s.urgent);
  const setUrgent = useStore((s) => s.setUrgent);
  const prints = useStore((s) => s.prints);
  const totals = useTotals();
  const cfg = pricing.urgency;
  if (!cfg) return null; // entry point is gated on the block — defensive only

  const mode = urgencyMode(toPrintSelections(pricedPrints(prints)));
  const fee = urgencyFeeCents(pricing, totals?.subtotal ?? 0) / 100;
  const cutoffLabel = formatCutoff(cfg.sameDayCutoff);
  const eta = computeUrgencyEta(
    sydneyNow(),
    parseCutoffMinutes(cfg.sameDayCutoff),
    cfg.productionDays,
  );

  const confirm = () => {
    setUrgent(true);
    onClose();
  };

  return (
    <CartModal
      title={cfg.label}
      caption="Move your order to the front of the print queue."
      width={520}
      onClose={onClose}
    >
      <div className="urgd">
        {mode === 'paid' ? (
          <>
            <p className="urgd__copy">
              Printed first, ahead of the standard queue, at double the price.
            </p>
            <div className="urgd__fee">+{formatMoney(fee)} added to your order</div>
            <p className="urgd__copy">
              {eta.startsToday
                ? `It's before today's ${cutoffLabel} cutoff, so we start yours today.`
                : eta.productionToday
                  ? `Today's ${cutoffLabel} cutoff has passed, so yours is printed first thing ${eta.startLabelLower}.`
                  : `We print Monday to Friday, so yours is printed first thing ${eta.startLabelLower}.`}
            </p>
            <table className="urgd__eta">
              <thead>
                <tr>
                  <th>Getting it</th>
                  <th>Ready</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Pick up, Sydney</td>
                  <td>{eta.startLabel}</td>
                </tr>
                <tr>
                  <td>Pick up, Wollongong</td>
                  <td>{eta.wollongongLabel}</td>
                </tr>
                <tr>
                  <td>Shipping</td>
                  <td>Printed {eta.startLabelLower}, then Express</td>
                </tr>
              </tbody>
            </table>
            <p className="urgd__foot">
              Wollongong prints are made in Sydney and sent down overnight. Shipped orders go
              Express, so delivery time depends on where you are.
            </p>
          </>
        ) : (
          <>
            <p className="urgd__copy">{cfg.copyRequest}</p>
            <div className="urgd__fee">Urgency fee {formatMoney(fee)}. Not charged today.</div>
          </>
        )}

        <div className="urgd__actions">
          {urgent && (
            <button
              type="button"
              className="linkbtn upload__delete urgd__clear"
              onClick={() => {
                setUrgent(false);
                onClose();
              }}
            >
              Remove urgent processing
            </button>
          )}
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={confirm}>
            Accept
          </button>
        </div>
      </div>
    </CartModal>
  );
}
